/**
 * PuckLetsGetYouBookedMacroPaymentLinkComponent
 *
 * Renders the "Let's Get You Booked - Payment Link" macro using the Puck template system.
 * This component loads the template from the database and renders it with actual reservation data.
 * Includes payment provider selection (Stripe/Omise), deposit percentage controls,
 * and dynamic payment link generation.
 *
 * Template key: @MACROS/LETS-GET-YOU-BOOKED_PAYMENT-LINK
 *
 * Key differences from the non-payment-link version:
 * - Integrates dynamic payment link generation via API
 * - Payment terms use 4 weeks prior to arrival (instead of 6)
 * - Provider selection (Stripe/Omise)
 * - Shows full and partial payment links
 */

import React, { useState, useEffect, useRef, useCallback, useMemo, KeyboardEvent } from 'react';
import { Render, type Data as PuckData } from '@measured/puck';
import { format, differenceInHours, startOfDay, addWeeks, isBefore } from 'date-fns';

import {
    multiplyAndRemoveDecimal,
    ReservationState,
    getDisplayName,
    ReservationPosItem,
} from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/model';
import {
    getProgramTypeDisplayName,
    getRoomTypeDisplayName,
} from '@digitalopsdev/serviceops-reservations-components/dist/EditReservation/OrderDetailsComponent';

import { useModelState } from '../../../../js/useModelState';
import { countProgramDaysBetween, longToMoney } from '../../../../js/utils';
import { MacroCopyButtons } from './MacroCopyButtons';
import { getFullPaymentFeePercent, getPartialPaymentFeePercent } from './paymentFeeRates';

// Import Puck infrastructure
import { getMacroPuckConfig } from 'phuketfit-components/dist/MacroPuck/MacroPuckConfig';
import { loadMacroPageData } from '../../MacroPuck/macroPuckClient';
import {
    MacroBindingProvider,
    createBindingValues,
    type MacroBindingValues,
} from 'phuketfit-components/dist/MacroPuck/MacroBindingContext';
import {
    MacroCollectionsProvider,
    type MacroCollectionsValue,
    type ProgramBookingItem,
    type RoomBookingItem,
    type PosItemData,
} from 'phuketfit-components/dist/MacroPuck/MacroCollectionContext';

// Template key for this macro
const TEMPLATE_KEY = '@MACROS/LETS-GET-YOU-BOOKED_PAYMENT-LINK';

// Payment link types
enum PaymentLinkType {
    Full = 0,
    Partial = 1,
}

// API response structure
interface PaymentLinkResultItem {
    paymentType: PaymentLinkType;
    url: string | null;
    error: string | null;
}

interface GeneratePaymentLinksResponse {
    paymentLinkResults: PaymentLinkResultItem[];
}

export interface PuckLetsGetYouBookedMacroPaymentLinkComponentProps {
    reservationState: ReservationState;
}

export function PuckLetsGetYouBookedMacroPaymentLinkComponent({
    reservationState,
}: PuckLetsGetYouBookedMacroPaymentLinkComponentProps) {
    // Ensure component re-renders if reservationState.Person changes
    useModelState(reservationState.Person);

    // State for loading and errors
    const [status, setStatus] = useState<
        'loading' | 'generatingLinks' | 'generatingPartialLink' | 'error' | 'ready'
    >('loading');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [pageData, setPageData] = useState<PuckData | null>(null);

    // State for computed values
    const [firstName, setFirstName] = useState<string>('');

    // State for links, deposit, provider
    const [fullPaymentLink, setFullPaymentLink] = useState<string | null>(null);
    const [partialPaymentLink, setPartialPaymentLink] = useState<string | null>(null);
    const [depositPercent, setDepositPercent] = useState<number>(50);
    const [depositPercentInput, setDepositPercentInput] = useState<string>('50');
    const [isDepositPercentValid, setIsDepositPercentValid] = useState<boolean>(true);
    const [provider, setProvider] = useState<'Omise' | 'Stripe'>('Stripe');
    const [partialLinkCache, setPartialLinkCache] = useState<
        Record<string, Record<number, string | null>>
    >({ Omise: {}, Stripe: {} });

    // Ref for copy functionality
    const previewRef = useRef<HTMLDivElement>(null);

    // Ref to store calculated values
    const bookingDataRef = useRef<{
        totalPrice: number;
        reservationNumber: number | null;
    } | null>(null);

    // Load template data
    useEffect(() => {
        let isMounted = true;

        async function loadTemplate() {
            try {
                const data = await loadMacroPageData(TEMPLATE_KEY);
                if (isMounted) {
                    setPageData(data);
                }
            } catch (error: any) {
                if (isMounted) {
                    console.error('Failed to load Puck template:', error);
                    setErrorMessage(error?.message ?? 'Failed to load template');
                    setStatus('error');
                }
            }
        }

        loadTemplate();

        return () => {
            isMounted = false;
        };
    }, []);

    // Calculate total price (stored as integer cents/satang)
    const totalPrice = useMemo(() => {
        return multiplyAndRemoveDecimal(reservationState.AmountPayableTotal.value);
    }, [reservationState.AmountPayableTotal.value]);

    // Store booking data in ref
    useEffect(() => {
        bookingDataRef.current = {
            totalPrice,
            reservationNumber: reservationState.ReservationNumber,
        };
    }, [totalPrice, reservationState.ReservationNumber]);

    // API Call Helper
    const callGenerateLinksApi = useCallback(
        async (
            requests: { paymentType: PaymentLinkType; depositPercent?: number; feePercent: number }[],
            apiProvider: 'Omise' | 'Stripe'
        ): Promise<PaymentLinkResultItem[] | null> => {
            if (!bookingDataRef.current) {
                setErrorMessage('Booking data not calculated yet.');
                return null;
            }
            const { totalPrice: tp, reservationNumber } = bookingDataRef.current;
            if (!reservationNumber) {
                setErrorMessage('Reservation number is missing.');
                return null;
            }
            if (tp <= 0) {
                setErrorMessage('Total price must be positive.');
                return null;
            }

            const totalAmountDecimal = tp / 100;

            try {
                const response = await fetch('/api/reservations/generate-payment-links', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        reservationNumber: String(reservationNumber),
                        totalAmount: totalAmountDecimal,
                        paymentLinkRequests: requests,
                        provider: apiProvider,
                    }),
                });
                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(
                        `API Error (${response.status}): ${errorText || 'Failed to generate links'}`
                    );
                }
                const data: GeneratePaymentLinksResponse = await response.json();
                return data.paymentLinkResults;
            } catch (error: any) {
                console.error('Error calling GeneratePaymentLinks API:', error);
                setErrorMessage(error.message || 'An unexpected error occurred.');
                return null;
            }
        },
        []
    );

    // Main Effect for Loading Data and Initial Links
    useEffect(() => {
        let isMounted = true;

        async function initializeComponent() {
            setStatus('loading');
            setErrorMessage(null);
            setFirstName('');

            // 1. Fetch Name
            const person = reservationState.Person.value;
            try {
                const name = person
                    ? await getDisplayName(person, reservationState)
                    : 'Client';
                if (!isMounted) return;

                const nameParts = (name || '').split(' ');
                const currentFirstName = nameParts[0] || 'Client';
                setFirstName(currentFirstName);

                // 2. Validate
                if (!reservationState.ReservationNumber) {
                    throw new Error('Reservation number is missing.');
                }
                if (totalPrice <= 0) {
                    throw new Error('Total price must be positive.');
                }

                // Wait for page data
                if (!pageData) {
                    return; // Will re-run when pageData is ready
                }

                // 3. Fetch Initial Links
                setStatus('generatingLinks');
                const initialRequests = [
                    { paymentType: PaymentLinkType.Full, feePercent: getFullPaymentFeePercent(provider) },
                    { paymentType: PaymentLinkType.Partial, depositPercent: depositPercent, feePercent: getPartialPaymentFeePercent(provider, depositPercent) },
                ];
                const results = await callGenerateLinksApi(initialRequests, provider);

                if (!isMounted) return;

                if (results) {
                    let genFullLink: string | null = null;
                    let genPartialLink: string | null = null;
                    let errors: string[] = [];

                    results.forEach((result) => {
                        if (result.error) {
                            errors.push(result.error);
                        } else if (result.paymentType === PaymentLinkType.Full) {
                            genFullLink = result.url;
                        } else if (result.paymentType === PaymentLinkType.Partial) {
                            genPartialLink = result.url;
                        }
                    });

                    if (errors.length > 0 || !genFullLink || !genPartialLink) {
                        setErrorMessage(
                            `Errors during link generation: ${errors.join('; ')}` ||
                                'Failed to generate one or both links.'
                        );
                        setStatus('error');
                    } else {
                        setFullPaymentLink(genFullLink);
                        setPartialPaymentLink(genPartialLink);
                        setPartialLinkCache((prev) => ({
                            ...prev,
                            [provider]: { ...prev[provider], [depositPercent]: genPartialLink },
                        }));
                        setStatus('ready');
                    }
                } else {
                    setStatus('error');
                }
            } catch (error: any) {
                if (!isMounted) return;
                console.error('Error during initialization:', error);
                setErrorMessage(error.message || 'Failed to initialize component.');
                setStatus('error');
            }
        }

        initializeComponent();

        return () => {
            isMounted = false;
        };
    }, [reservationState, callGenerateLinksApi, provider, pageData, totalPrice, depositPercent]);

    // Calculate earliest date from reservations (4 weeks for payment link version)
    const earliestDate = useMemo(() => {
        let earliest: Date | null = null;

        for (const program of reservationState.ProgramBookings.toArray()) {
            const programStartOfDay = startOfDay(program.Start.value);
            if (!earliest || isBefore(programStartOfDay, earliest)) {
                earliest = programStartOfDay;
            }
        }

        for (const room of reservationState.RoomBookings.toArray()) {
            const roomStartOfDay = startOfDay(room.CheckIn.value);
            if (!earliest || isBefore(roomStartOfDay, earliest)) {
                earliest = roomStartOfDay;
            }
        }

        return earliest;
    }, [reservationState]);

    // Calculate if payment due date has passed (4 weeks before arrival)
    const isPaymentDueDatePassed = useMemo(() => {
        if (!earliestDate) return false;
        const fullPaymentDueBy = startOfDay(addWeeks(earliestDate, -4));
        return isBefore(fullPaymentDueBy, new Date());
    }, [earliestDate]);

    // Calculate full payment due date text
    const fullPaymentDueByText = useMemo(() => {
        if (!earliestDate) return '';

        let fullPaymentDueBy = startOfDay(addWeeks(earliestDate, -4));
        if (isBefore(fullPaymentDueBy, new Date())) {
            fullPaymentDueBy = earliestDate;
        }

        return format(fullPaymentDueBy, 'MMMM d, yyyy');
    }, [earliestDate]);

    // Transform reservation data into binding values
    const bindingValues = useMemo((): MacroBindingValues | null => {
        if (!firstName || !fullPaymentLink) return null;

        const depositAmount = Math.round(totalPrice * (depositPercent / 100));

        return createBindingValues({
            FirstName: firstName,
            DepositPercent: depositPercent,
            DepositAmount: `฿${longToMoney(depositAmount)}`,
            TotalPrice: `฿${longToMoney(totalPrice)}`,
            FullPaymentDueDate: fullPaymentDueByText,
            PaymentLinkFull: fullPaymentLink,
            PaymentLinkPartial: partialPaymentLink ?? '',
            PaymentProvider: provider,
            FullFeePercent: String(getFullPaymentFeePercent(provider)),
            PartialFeePercent: String(getPartialPaymentFeePercent(provider, depositPercent)),
            IsPaymentDueDatePassed: isPaymentDueDatePassed,
        });
    }, [
        firstName,
        totalPrice,
        depositPercent,
        fullPaymentDueByText,
        fullPaymentLink,
        partialPaymentLink,
        provider,
        isPaymentDueDatePassed,
    ]);

    // Transform reservation data into collections
    const collections = useMemo((): MacroCollectionsValue => {
        // Transform programs
        const programs = reservationState.ProgramBookings.toArray();
        const transformedPrograms: Record<string, ProgramBookingItem & { priceRaw: number }> = {};

        for (const program of programs) {
            const programName = getProgramTypeDisplayName(reservationState, program.ProgramType.value);
            const programPrice = multiplyAndRemoveDecimal(program.Price.price.value.payableAmount);
            const programDays = countProgramDaysBetween(program.Start.value, program.End.value);
            const programStartDate = format(program.Start.value, 'MMMM d, yyyy');
            const programEndDate = format(program.End.value, 'MMMM d, yyyy');

            const groupKey = `${programName}_${programStartDate}_${programEndDate}`;
            const existing = transformedPrograms[groupKey];

            if (!existing) {
                transformedPrograms[groupKey] = {
                    Name: programName,
                    Days: programDays,
                    StartDate: programStartDate,
                    EndDate: programEndDate,
                    Price: `฿${longToMoney(programPrice)}`,
                    Quantity: 1,
                    priceRaw: programPrice,
                };
            } else {
                existing.priceRaw += programPrice;
                existing.Price = `฿${longToMoney(existing.priceRaw)}`;
                existing.Quantity += 1;
            }
        }

        // Transform rooms
        const rooms = reservationState.RoomBookings.toArray();
        const transformedRooms: Record<string, RoomBookingItem & { priceRaw: number }> = {};

        for (const room of rooms) {
            const roomName = getRoomTypeDisplayName(reservationState, room.RoomType.value);
            const roomPrice = multiplyAndRemoveDecimal(room.CalculatedPrice.value.totalPayableAmount);
            const roomNights = Math.ceil(differenceInHours(room.CheckOut.value, room.CheckIn.value) / 24) | 0;
            const roomCheckInDate = format(room.CheckIn.value, 'MMMM d, yyyy');
            const roomCheckOutDate = format(room.CheckOut.value, 'MMMM d, yyyy');

            const groupKey = `${roomName}_${roomCheckInDate}_${roomCheckOutDate}`;
            const existing = transformedRooms[groupKey];

            if (!existing) {
                transformedRooms[groupKey] = {
                    RoomType: roomName,
                    Nights: roomNights,
                    CheckInDate: roomCheckInDate,
                    CheckOutDate: roomCheckOutDate,
                    Price: `฿${longToMoney(roomPrice)}`,
                    Quantity: 1,
                    priceRaw: roomPrice,
                };
            } else {
                existing.priceRaw += roomPrice;
                existing.Price = `฿${longToMoney(existing.priceRaw)}`;
                existing.Quantity += 1;
            }
        }

        // Transform POS items
        const posItems = reservationState.PosItems.value;
        const transformedPosItems: PosItemData[] = posItems.map((item: ReservationPosItem) => ({
            Description: item.Description,
            Quantity: item.Quantity,
            Price: `฿${longToMoney(multiplyAndRemoveDecimal(item.Amount * item.Quantity))}`,
        }));

        return {
            programs: Object.values(transformedPrograms).map(({ priceRaw, ...rest }) => rest),
            rooms: Object.values(transformedRooms).map(({ priceRaw, ...rest }) => rest),
            posItems: transformedPosItems,
        };
    }, [reservationState]);

    // Generate Partial Link function
    const generatePartialLink = useCallback(
        async (newDepositPercent: number) => {
            if (!bookingDataRef.current || !firstName || !fullPaymentLink || status === 'generatingLinks')
                return;

            // Handle 100% deposit case
            if (newDepositPercent === 100) {
                setPartialPaymentLink(null);
                setDepositPercent(100);
                setStatus('ready');
                return;
            }

            // Check cache first
            if (partialLinkCache[provider] && partialLinkCache[provider][newDepositPercent]) {
                setPartialPaymentLink(partialLinkCache[provider][newDepositPercent]);
                setDepositPercent(newDepositPercent);
                setStatus('ready');
                return;
            }

            // Not in cache, generate via API
            setStatus('generatingPartialLink');
            const partialRequest = [
                { paymentType: PaymentLinkType.Partial, depositPercent: newDepositPercent, feePercent: getPartialPaymentFeePercent(provider, newDepositPercent) },
            ];

            const results = await callGenerateLinksApi(partialRequest, provider);

            if (results && results.length > 0) {
                const partialResult = results.find((r) => r.paymentType === PaymentLinkType.Partial);
                if (partialResult?.error) {
                    setErrorMessage(
                        (prev) =>
                            (prev ? `${prev}; ` : '') + `Partial Link Update Error: ${partialResult.error}`
                    );
                    setStatus('error');
                } else if (partialResult?.url) {
                    setPartialPaymentLink(partialResult.url);
                    setPartialLinkCache((prevCache) => ({
                        ...prevCache,
                        [provider]: { ...prevCache[provider], [newDepositPercent]: partialResult.url },
                    }));
                    setDepositPercent(newDepositPercent);
                    setErrorMessage(null);
                    setStatus('ready');
                } else {
                    setErrorMessage('Partial link generation failed silently after update.');
                    setStatus('error');
                }
            } else {
                setErrorMessage(
                    (prev) => (prev ? `${prev}; ` : '') + 'Failed to get partial link update response.'
                );
                setStatus('error');
            }
        },
        [firstName, fullPaymentLink, callGenerateLinksApi, status, partialLinkCache, provider]
    );

    // Handlers for Input Change, Blur, and KeyDown
    const handleDepositInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const valueStr = e.target.value;
        setDepositPercentInput(valueStr);
        const valueNum = parseFloat(valueStr);
        if (!isNaN(valueNum) && valueNum >= 0 && valueNum <= 100) {
            setIsDepositPercentValid(true);
        } else {
            setIsDepositPercentValid(false);
        }
    };

    const triggerLinkGeneration = () => {
        if (!isDepositPercentValid) return;
        const valueNum = parseFloat(depositPercentInput);
        if (isNaN(valueNum) || valueNum < 0 || valueNum > 100) {
            setIsDepositPercentValid(false);
            return;
        }

        setDepositPercent(valueNum);
        generatePartialLink(valueNum);
    };

    const handleDepositInputBlur = () => {
        triggerLinkGeneration();
    };

    const handleDepositInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            triggerLinkGeneration();
            (e.target as HTMLInputElement).blur();
        }
    };

    // Get the Puck config
    const config = useMemo(() => getMacroPuckConfig(), []);

    // Render loading state
    if (status === 'loading' || status === 'generatingLinks') {
        return <div className="p-4">Loading booking details and payment links...</div>;
    }

    const showControls = status === 'ready' || status === 'generatingPartialLink' || status === 'error';
    const isUpdatingPartial = status === 'generatingPartialLink';

    return (
        <div className="flex flex-col gap-4">
            {/* Error Display */}
            {status === 'error' && (
                <div className="p-4 text-red-600 border border-red-600 rounded">
                    Error: {errorMessage || 'An unknown error occurred.'}
                </div>
            )}

            {/* Provider and Deposit Input Area */}
            {showControls && (
                <div className="flex flex-wrap gap-4 items-start">
                    {/* Provider Selection */}
                    <div>
                        <label className="text-sm font-semibold">Payment Provider</label>
                        <div className="flex items-center gap-4 mt-1">
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                    type="radio"
                                    name="provider"
                                    value="Stripe"
                                    checked={provider === 'Stripe'}
                                    onChange={() => setProvider('Stripe')}
                                    className="form-check-input"
                                    disabled={isUpdatingPartial}
                                />
                                Stripe
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer">
                                <input
                                    type="radio"
                                    name="provider"
                                    value="Omise"
                                    checked={provider === 'Omise'}
                                    onChange={() => setProvider('Omise')}
                                    className="form-check-input"
                                    disabled={isUpdatingPartial}
                                />
                                Omise
                            </label>
                        </div>
                    </div>

                    {/* Deposit Percentage */}
                    <div>
                        <label htmlFor="depositPercentInput" className="text-sm font-semibold">
                            Deposit Percentage
                        </label>
                        <div className="flex items-center gap-2 mt-1">
                            <input
                                id="depositPercentInput"
                                type="number"
                                min="0"
                                max="100"
                                value={depositPercentInput}
                                onChange={handleDepositInputChange}
                                onBlur={handleDepositInputBlur}
                                onKeyDown={handleDepositInputKeyDown}
                                className={`form-control w-24 ${!isDepositPercentValid ? 'border-red-500' : ''} ${
                                    isUpdatingPartial ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                disabled={isUpdatingPartial}
                            />{' '}
                            %
                            {isUpdatingPartial && (
                                <span className="text-blue-600 text-sm ml-2">Updating link...</span>
                            )}
                        </div>
                        {!isDepositPercentValid && (
                            <div className="text-red-500 text-sm mt-1">
                                Please enter a percentage between 0 and 100.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Preview Buttons Area */}
            {showControls && (
                <div className="flex justify-between items-center">
                    <div className="flex gap-2">
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => fullPaymentLink && window.open(fullPaymentLink, '_blank')}
                            disabled={!fullPaymentLink || isUpdatingPartial}
                            title={!fullPaymentLink ? 'Full link not available' : 'Open full payment link in new tab'}
                        >
                            Preview Full Link
                        </button>
                        {depositPercent !== 100 && (
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => partialPaymentLink && window.open(partialPaymentLink, '_blank')}
                                disabled={!partialPaymentLink || isUpdatingPartial}
                                title={
                                    !partialPaymentLink
                                        ? 'Partial link not available'
                                        : `Open ${depositPercent}% deposit link in new tab`
                                }
                            >
                                Preview {depositPercent}% Link
                            </button>
                        )}
                    </div>
                    <MacroCopyButtons
                        getHtml={() => previewRef.current?.innerHTML ?? null}
                        getText={() => previewRef.current?.textContent ?? null}
                    />
                </div>
            )}

            {/* Rendered Template */}
            <style>
                {`
                    .puck-macro-preview {
                        font-family: Arial, sans-serif;
                        font-size: 12px;
                        line-height: 22px;
                        color: #333;
                    }
                    .puck-macro-preview * { line-height: 22px; }
                    /* Zendesk CKEditor style: direct children get 8px margins, p has no margin */
                    .puck-macro-preview > * { margin-top: 8px; margin-bottom: 8px; }
                    .puck-macro-preview > *:first-child { margin-top: 12px; }
                    .puck-macro-preview p { line-height: 22px; margin: 0; }
                    /* Zendesk: trailing <br> creates double line break */
                    .puck-macro-preview p:has(> br:last-child) { padding-bottom: 22px; }
                    .puck-macro-preview ul {
                      padding-left: 15px;
                      list-style-type: disc;
                      margin-left: 8px;
                      margin-top: 0;
                    }
                    .puck-macro-preview li { line-height: 22px; }
                    .puck-macro-preview hr { margin: 15px 0 !important; border: none; border-top: 1px solid #ccc; }
                    .puck-macro-preview a { color: #1264a3; text-decoration: underline; }
                    .puck-macro-preview strong { font-weight: bold; }
                    .puck-macro-preview em { font-style: italic; }
                    .puck-macro-preview u { text-decoration: underline; }
                `}
            </style>
            {(status === 'ready' || isUpdatingPartial) && pageData && bindingValues ? (
                <div
                    ref={previewRef}
                    className="puck-macro-preview"
                >
                    <MacroBindingProvider value={bindingValues}>
                        <MacroCollectionsProvider value={collections}>
                            <Render config={config} data={pageData} />
                        </MacroCollectionsProvider>
                    </MacroBindingProvider>
                </div>
            ) : (
                !errorMessage && <div className="p-4">Generating content...</div>
            )}
        </div>
    );
}
